import { useState, type ReactNode, type ElementType } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";

interface SectionProps {
  /** Heading rendered inside the `<h2>` */
  title: string;
  /** Optional Lucide-style component rendered before the title */
  icon?: ElementType;
  /** Tailwind text colour for the heading icon, e.g. `text-blue-500` */
  iconColor?: string;
  /**
   * Tailwind border colour for the 3px left rail, e.g. `border-blue-500`.
   * When set, the card uses `rounded-r-lg` so the left edge butts flush against
   * the rail. Pass `undefined` (or omit) for a railless section.
   */
  accentColor?: string;
  /** Right-aligned slot in the header row — typically a ghost icon-only Button */
  headerRight?: ReactNode;
  /** Inline element after the title text — usually a count Badge */
  titleSuffix?: ReactNode;
  /**
   * Optional row rendered below the header (filter chips, qty inputs, etc.).
   * Sits inside the header band so it inherits the same padding rhythm.
   */
  headerBelow?: ReactNode;
  /** When true, clicking the header toggles content visibility. */
  collapsible?: boolean;
  /** When true and `collapsible`, the section starts collapsed. */
  defaultCollapsed?: boolean;
  /** Stable id appended to the `data-testid` attribute on the outer wrapper. */
  "data-testid"?: string;
  children: ReactNode;
  contentClassName?: string;
  className?: string;
}

/**
 * Section primitive
 *
 * Canonical reference: ianfiles/ui-design-system.md §2.6 + §3.1
 *
 * The shell for every information block on a detail page. Replaces hand-rolled
 * `border-l-[3px] bg-card rounded-r-lg` divs. Padding rhythm baked in:
 *   - Header: `px-4 py-3`
 *   - Body: `px-4 pb-4`
 *
 * Header rules from §3.1:
 *   - Section-level Edit / Add buttons render as `variant="ghost" size="icon"`
 *     in `headerRight`. Never use labelled `Edit Section` / `Add Item` text.
 *   - The accent rail's colour MUST be one of the curated palette in
 *     Appendix A. Adding a new colour without semantic justification dilutes
 *     the system.
 */
export function Section({
  title,
  icon: Icon,
  iconColor = "text-muted-foreground",
  accentColor,
  headerRight,
  titleSuffix,
  headerBelow,
  collapsible = false,
  defaultCollapsed = false,
  "data-testid": dataTestId,
  children,
  contentClassName,
  className,
}: SectionProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const hasRail = Boolean(accentColor);
  const showBody = !collapsible || !collapsed;

  return (
    <div
      data-testid={dataTestId}
      className={cn(
        "bg-card border border-card-border shadow-sm",
        hasRail ? "rounded-r-lg border-l-[3px]" : "rounded-lg",
        hasRail && accentColor,
        className
      )}
    >
      <div className="flex flex-col">
        {/* Header row */}
        <div className="flex items-center justify-between gap-2 px-4 py-3">
          {collapsible ? (
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              aria-expanded={!collapsed}
              className={cn(
                "flex items-center gap-2 rounded-sm",
                // Negative inset + matching padding so the elevation overlay
                // extends slightly past the text and feels clickable
                "-mx-1 px-1 py-0.5",
                "hover-elevate active-elevate-2"
              )}
            >
              <ChevronDown
                className={cn(
                  "h-4 w-4 text-muted-foreground transition-transform",
                  collapsed && "-rotate-90"
                )}
              />
              <h2 className="text-sm font-semibold flex items-center gap-2">
                {Icon && <Icon className={cn("h-4 w-4", iconColor)} />}
                {title}
                {titleSuffix}
              </h2>
            </button>
          ) : (
            <h2 className="text-sm font-semibold flex items-center gap-2">
              {Icon && <Icon className={cn("h-4 w-4", iconColor)} />}
              {title}
              {titleSuffix}
            </h2>
          )}
          {headerRight}
        </div>

        {/* Optional `headerBelow` strip (filters, density toggles, etc.) */}
        {headerBelow && showBody ? (
          <div className="px-4 pb-3">{headerBelow}</div>
        ) : null}
      </div>

      {showBody ? (
        <div className={cn("px-4 pb-4", contentClassName)}>{children}</div>
      ) : null}
    </div>
  );
}
